// Shared fetch helper for training admin actions (split for file-size hygiene).
import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";

export const ADMIN_ACTION = "/api/admin/action";

export type FetchResult<T> = { ok: true; status: number; data: T } | { ok: false; status: number; error: string };

export function normalizeTrainingError(raw: string): string {
  const msg = String(raw || "").trim();
  if (!msg) return "Training request failed.";
  if (/FUNCTION_INVOCATION_FAILED|iad1::/i.test(msg)) {
    return "Training service failed to run. Please retry in a moment. If this keeps happening, check Vercel function logs and training env vars.";
  }
  return msg;
}

export async function trainingFetch<T>(path: string, init: RequestInit = {}): Promise<FetchResult<T>> {
  if (!supabase) return { ok: false, status: 503, error: "App is not configured for sign-in." };

  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: "Sign in to use training." };

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(init.headers as Record<string, string> | undefined),
  };
  if (init.body != null && !headers["Content-Type"] && !headers["content-type"]) {
    headers["Content-Type"] = "application/json";
  }
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) headers["X-Workspace-Organization-Id"] = ws;

  let r: Response;
  try {
    r = await fetch(path, { ...init, headers });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return { ok: false, status: 0, error: normalizeTrainingError(message) };
  }
  const text = await r.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text) as T;
  } catch {
    /* non-JSON */
  }

  if (!r.ok) {
    const errRaw =
      json && typeof json === "object" && json !== null && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : text || `HTTP ${r.status}`;
    return { ok: false, status: r.status, error: normalizeTrainingError(errRaw) };
  }

  return { ok: true, status: r.status, data: json as T };
}
